/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { Box, Card, Group, HoverCard, HoverCardProps, Loader, Text } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { useDisclosure } from '@mantine/hooks'
import Amount from '../transaction/Amount'
import { fetchEstate } from '../../lib/rest/estateAPIClient'
import EstatePhoto from './EstatePhoto'
import Ref from '../Ref'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import { getEstateLink } from '../../lib/links/estateLinks'
import { FormattedNumber, useIntl } from 'react-intl'
import FurnishingAndEquipmentLabel from '../labels/FurnishingAndEquipmentLabel'
import LabelledValue from '../LabelledValue'

export type EstateInfoHoverCardProps = HoverCardProps & {
  estateId: number,
}

function EstateInfoHoverCard (props: EstateInfoHoverCardProps) {
  const {
    children,
    estateId,
    ...others
  } = props

  const intl = useIntl()
  const [opened, { toggle }] = useDisclosure()

  const estateQuery = useQuery({
    enabled: estateId != null && opened,
    queryKey: ['estate', estateId],
    queryFn: () => fetchEstate(estateId),
    refetchOnWindowFocus: false
  })
  const estate = estateQuery.data

  return (
    <HoverCard
      openDelay={500}
      closeDelay={200}
      onOpen={toggle}
      onClose={toggle}
      position="top"
      {...others}
    >
      <HoverCard.Target>
        {children}
      </HoverCard.Target>

      <HoverCard.Dropdown
        w="100%"
        maw={300}
        p={estateQuery.isLoading ? undefined : 0}
      >
        {estateQuery.isLoading
          ? <Loader size="sm" />
          : null}

        {estate != null
          ? <Card>
            <Card.Section>
              <EstatePhoto
                height={150}
                photos={estate.Photos}
              />
            </Card.Section>

            <Box mt="md">
              <Group gap="xs">
                <AnchorLink to={getEstateLink(estateId)}>
                  <Ref fw="bold" value={estate.reference} />
                </AnchorLink>
                <Amount ms="auto" inherit value={estate.price} withCurrency />
              </Group>

              <Box fz="sm">
                <Text inherit>
                  {estate.City?.name}

                  {estate.residence != null
                    ? <> - {estate.residence}</>
                    : null}
                </Text>

                <FurnishingAndEquipmentLabel
                  furnished={estate.furnished}
                  equipped={estate.equipped}
                />

                {estate.surface > 0
                  ? <LabelledValue
                    label={intl.formatMessage({
                      id: 'surface',
                      defaultMessage: 'Superficie'
                    })}
                    value={<><FormattedNumber
                      value={estate.surface}
                      style="unit"
                      unit="meter"
                    />²</>}
                  />
                  : null}

                {estate.landArea > 0
                  ? <LabelledValue
                    label={intl.formatMessage({
                      id: 'terrain',
                      defaultMessage: 'Terrain'
                    })}
                    value={<><FormattedNumber
                      value={estate.landArea}
                      style="unit"
                      unit="meter"
                    />²</>}
                  />
                  : null}

                {estate.rooms > 0
                  ? <LabelledValue
                    label={intl.formatMessage({
                      id: 'rooms',
                      defaultMessage: 'Pièces'
                    })}
                    value={estate.rooms}
                  />
                  : null}
              </Box>
            </Box>
          </Card>
          : null}
      </HoverCard.Dropdown>
    </HoverCard>
  )
}

export default EstateInfoHoverCard
